import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    CmsContentUpdateRequest,
    CmsImproveContentRequest,
    CmsMicrositeCreateRequest,
    CmsPublishRequest,
    CmsRenderArtifactStatus,
    CmsRenderArtifactView,
    CmsReviewDecisionRequest,
    CmsSubmitRequest,
    GUID,
    InternalUserProfile,
    MultipartFile,
} from '@ndvr/cms-api';

import { CmsApi } from 'API';
import { API_ROOT } from 'env';
import { Toast } from 'widgets';

export const CmsQueryKeys = {
    all: ['cms'] as const,
    homepageModuleDescriptors: () => [...CmsQueryKeys.all, 'homepage-module-descriptors'] as const,
    list: (ownerUserGUID?: string) => [...CmsQueryKeys.all, 'microsites', ownerUserGUID ?? 'all'] as const,
    detail: (cmsMicrositeGUID: string) => [...CmsQueryKeys.all, 'microsite', cmsMicrositeGUID] as const,
    homepage: (cmsMicrositeGUID: string) => [...CmsQueryKeys.detail(cmsMicrositeGUID), 'homepage'] as const,
    history: (cmsMicrositeGUID: string) => [...CmsQueryKeys.detail(cmsMicrositeGUID), 'history'] as const,
    publishStatus: (cmsMicrositeGUID: string) => [...CmsQueryKeys.detail(cmsMicrositeGUID), 'publish-status'] as const,
    previewVersion: (cmsContentVersionGUID: string) =>
        [...CmsQueryKeys.all, 'content-version', cmsContentVersionGUID, 'preview'] as const,
    versionSecReview: (cmsContentVersionGUID: string) =>
        [...CmsQueryKeys.all, 'content-version', cmsContentVersionGUID, 'sec-review'] as const,
    draftSecReviewRoot: (cmsMicrositeGUID: string) =>
        [...CmsQueryKeys.detail(cmsMicrositeGUID), 'draft-sec-review'] as const,
    draftSecReview: (cmsMicrositeGUID: string, draftUpdatedAt?: string) =>
        [...CmsQueryKeys.draftSecReviewRoot(cmsMicrositeGUID), draftUpdatedAt ?? 'unknown'] as const,
    assetPreview: (cmsAssetGUID: string) => [...CmsQueryKeys.all, 'asset', cmsAssetGUID, 'preview'] as const,
};

const isPendingCmsRender = (artifact?: CmsRenderArtifactView): boolean =>
    artifact?.status === CmsRenderArtifactStatus.REQUESTED || artifact?.status === CmsRenderArtifactStatus.RENDERING;

export const cmsRenderArtifactSiteUrl = (cmsRenderArtifactGUID: string): string =>
    `${API_ROOT}/cms/api/cms/render-artifacts/${cmsRenderArtifactGUID}/site/index.html`;

export const cmsRenderArtifactShareSiteUrl = (token: string): string =>
    `${API_ROOT}/cms/api/cms/render-artifact-shares/site/index.html?token=${encodeURIComponent(token)}`;

export const useCreateCmsRenderArtifactShareLink = () =>
    useMutation({
        mutationFn: async (cmsRenderArtifactGUID: string) => {
            const [response] = await CmsApi({ hideError: true }).createRenderArtifactShareLink(cmsRenderArtifactGUID);
            if (!response) {
                throw new Error('Failed to create CMS share link.');
            }
            return response;
        },
    });

export const useCmsHomepageModuleDescriptors = () =>
    useQuery({
        queryKey: CmsQueryKeys.homepageModuleDescriptors(),
        retry: false,
        queryFn: async () => {
            const [response] = await CmsApi().getHomepageModuleDescriptors();
            return response ?? [];
        },
    });

export const useCmsMicrosites = (ownerUserGUID?: GUID<InternalUserProfile>, options: { enabled?: boolean } = {}) =>
    useQuery({
        queryKey: CmsQueryKeys.list(ownerUserGUID),
        enabled: options.enabled ?? true,
        retry: false,
        queryFn: async () => {
            const [response] = await CmsApi().listMicrosites(ownerUserGUID ? { ownerUserGUID } : {});
            return response ?? [];
        },
    });

export const useCmsMicrosite = (cmsMicrositeGUID: string | undefined) =>
    useQuery({
        queryKey: CmsQueryKeys.detail(cmsMicrositeGUID ?? ''),
        enabled: !!cmsMicrositeGUID,
        retry: false,
        queryFn: async () => {
            const [response] = await CmsApi().getMicrosite(cmsMicrositeGUID!);
            return response ?? undefined;
        },
    });

export const useCmsHomepageContent = (cmsMicrositeGUID: string | undefined) =>
    useQuery({
        queryKey: CmsQueryKeys.homepage(cmsMicrositeGUID ?? ''),
        enabled: !!cmsMicrositeGUID,
        retry: false,
        queryFn: async () => {
            const [response] = await CmsApi().getHomepageContent(cmsMicrositeGUID!);
            return response ?? undefined;
        },
    });

export const cmsHistoryQueryOptions = (cmsMicrositeGUID: string | undefined) =>
    queryOptions({
        queryKey: CmsQueryKeys.history(cmsMicrositeGUID ?? ''),
        enabled: !!cmsMicrositeGUID,
        retry: false,
        refetchInterval: (query) =>
            query.state.data?.renderArtifacts.some((artifact) => isPendingCmsRender(artifact)) ? 3000 : false,
        queryFn: async () => {
            const [response] = await CmsApi().listHistory(cmsMicrositeGUID!);
            return response ?? undefined;
        },
    });

export const useCmsHistory = (cmsMicrositeGUID: string | undefined) =>
    useQuery(cmsHistoryQueryOptions(cmsMicrositeGUID));

export const useCmsPublishStatus = (cmsMicrositeGUID: string | undefined) =>
    useQuery({
        queryKey: CmsQueryKeys.publishStatus(cmsMicrositeGUID ?? ''),
        enabled: !!cmsMicrositeGUID,
        retry: false,
        queryFn: async () => {
            const [response] = await CmsApi().getPublishStatus(cmsMicrositeGUID!);
            return response ?? undefined;
        },
    });

export const useCmsVersionPreview = (cmsContentVersionGUID: string | undefined) =>
    useQuery({
        queryKey: CmsQueryKeys.previewVersion(cmsContentVersionGUID ?? ''),
        enabled: !!cmsContentVersionGUID,
        retry: false,
        refetchInterval: (query) => (isPendingCmsRender(query.state.data?.renderArtifact) ? 3000 : false),
        queryFn: async () => {
            const [response] = await CmsApi().previewVersion(cmsContentVersionGUID!);
            return response ?? undefined;
        },
    });

export const useCmsContentVersionSecReview = (cmsContentVersionGUID: string | undefined) =>
    useQuery({
        queryKey: CmsQueryKeys.versionSecReview(cmsContentVersionGUID ?? ''),
        enabled: !!cmsContentVersionGUID,
        retry: false,
        queryFn: async () => {
            const [response] = await CmsApi().getContentVersionSecReview(cmsContentVersionGUID!);
            return response ?? null;
        },
    });

export const useCmsDraftSecReview = (cmsMicrositeGUID: string | undefined, draftUpdatedAt: string | undefined) =>
    useQuery({
        queryKey: CmsQueryKeys.draftSecReview(cmsMicrositeGUID ?? '', draftUpdatedAt),
        enabled: !!cmsMicrositeGUID && !!draftUpdatedAt,
        retry: false,
        queryFn: async () => {
            const [response] = await CmsApi().getDraftSecReview(cmsMicrositeGUID!);
            return response ?? null;
        },
    });

export const useCreateCmsMicrosite = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: CmsMicrositeCreateRequest) => {
            const [response] = await CmsApi({ hideError: true }).createMicrosite(request);
            return response;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: CmsQueryKeys.all }),
        onError: (error) => Toast.forError(error, 'Failed to create CMS microsite.'),
    });
};

export const useUpdateCmsHomepageContent = (cmsMicrositeGUID: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: CmsContentUpdateRequest) => {
            const [response] = await CmsApi({ hideError: true }).updateHomepageContent(cmsMicrositeGUID, request);
            return response ?? undefined;
        },
        onSuccess: () => {
            Toast.success('Draft saved.');
            queryClient.invalidateQueries({ queryKey: CmsQueryKeys.homepage(cmsMicrositeGUID) });
            queryClient.invalidateQueries({ queryKey: CmsQueryKeys.detail(cmsMicrositeGUID) });
            queryClient.invalidateQueries({ queryKey: CmsQueryKeys.draftSecReviewRoot(cmsMicrositeGUID) });
        },
        onError: (error) => Toast.forError(error, 'Failed to save CMS draft.'),
    });
};

export const useImproveCmsHomepageModuleContent = (cmsMicrositeGUID: string) =>
    useMutation({
        mutationFn: async (request: CmsImproveContentRequest) => {
            const [response] = await CmsApi({ hideError: true }).improveHomepageModuleContent(
                cmsMicrositeGUID,
                request,
            );
            return response ?? undefined;
        },
        onError: (error) => Toast.forError(error, 'Failed to improve CMS content.'),
    });

export const useUploadCmsAsset = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: {
            cmsMicrositeGUID: string;
            altText?: string;
            caption?: string;
            fileToUpload: MultipartFile;
        }) => {
            const [response] = await CmsApi({ hideError: true }).uploadAsset(request);
            return response;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: CmsQueryKeys.all }),
        onError: (error) => Toast.forError(error, 'Failed to register CMS image asset.'),
    });
};

export const fetchCmsAssetContentBlob = async (cmsAssetGUID: string): Promise<Blob> => {
    const response = await fetch(`${API_ROOT}/cms/api/cms/assets/${cmsAssetGUID}/content`);
    if (!response.ok) {
        throw new Error('Failed to load CMS image asset.');
    }
    return response.blob();
};

export const fetchCmsAssetPreview = async (cmsAssetGUID: string) => {
    const [[asset], content] = await Promise.all([
        CmsApi({ hideError: true }).getAsset(cmsAssetGUID),
        fetchCmsAssetContentBlob(cmsAssetGUID),
    ]);
    if (!asset) {
        throw new Error('Failed to load CMS image asset.');
    }
    return { asset, content };
};

export const useSubmitCmsForReview = (cmsMicrositeGUID: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: CmsSubmitRequest) => {
            const [response] = await CmsApi({ hideError: true }).submitForReview(cmsMicrositeGUID, request);
            return response ?? undefined;
        },
        onSuccess: () => {
            Toast.success('Version created for review.');
            queryClient.invalidateQueries({ queryKey: CmsQueryKeys.detail(cmsMicrositeGUID) });
            queryClient.invalidateQueries({ queryKey: CmsQueryKeys.history(cmsMicrositeGUID) });
            queryClient.invalidateQueries({ queryKey: CmsQueryKeys.homepage(cmsMicrositeGUID) });
        },
        onError: (error) => Toast.forError(error, 'Failed to submit CMS draft for review.'),
    });
};

export const useApproveCmsVersion = (cmsMicrositeGUID: string, cmsContentVersionGUID: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (request: CmsReviewDecisionRequest) =>
            CmsApi({ hideError: true }).approve(cmsContentVersionGUID, request),
        onSuccess: () => {
            Toast.success('CMS version approved.');
            queryClient.invalidateQueries({ queryKey: CmsQueryKeys.history(cmsMicrositeGUID) });
            queryClient.invalidateQueries({ queryKey: CmsQueryKeys.previewVersion(cmsContentVersionGUID) });
        },
        onError: (error) => Toast.forError(error, 'Failed to approve CMS version.'),
    });
};

export const useRequestCmsChanges = (cmsMicrositeGUID: string, cmsContentVersionGUID: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (request: CmsReviewDecisionRequest) =>
            CmsApi({ hideError: true }).requestChanges(cmsContentVersionGUID, request),
        onSuccess: () => {
            Toast.success('Changes requested.');
            queryClient.invalidateQueries({ queryKey: CmsQueryKeys.history(cmsMicrositeGUID) });
            queryClient.invalidateQueries({ queryKey: CmsQueryKeys.previewVersion(cmsContentVersionGUID) });
        },
        onError: (error) => Toast.forError(error, 'Failed to request CMS changes.'),
    });
};

export const useCreateCmsSecReviewForVersion = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (cmsContentVersionGUID: string) => {
            const [response] = await CmsApi({ hideError: true }).createContentVersionSecReview(cmsContentVersionGUID);
            return response ?? undefined;
        },
        onSuccess: (_response, cmsContentVersionGUID) => {
            Toast.success('AI SEC pre-review generated.');
            queryClient.invalidateQueries({ queryKey: CmsQueryKeys.versionSecReview(cmsContentVersionGUID) });
        },
        onError: (error) => Toast.forError(error, 'Failed to generate AI SEC pre-review.'),
    });
};

export const useCreateCmsSecReviewForDraft = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (cmsMicrositeGUID: string) => {
            const [response] = await CmsApi({ hideError: true }).createDraftSecReview(cmsMicrositeGUID);
            return response ?? undefined;
        },
        onSuccess: (_response, cmsMicrositeGUID) => {
            Toast.success('AI SEC pre-review generated.');
            queryClient.invalidateQueries({ queryKey: CmsQueryKeys.draftSecReviewRoot(cmsMicrositeGUID) });
        },
        onError: (error) => Toast.forError(error, 'Failed to generate AI SEC pre-review.'),
    });
};

const usePublishMutation = (
    cmsMicrositeGUID: string,
    action: (request: CmsPublishRequest) => Promise<unknown>,
    successMessage: string,
    errorMessage: string,
) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: action,
        onSuccess: () => {
            Toast.success(successMessage);
            queryClient.invalidateQueries({ queryKey: CmsQueryKeys.detail(cmsMicrositeGUID) });
            queryClient.invalidateQueries({ queryKey: CmsQueryKeys.history(cmsMicrositeGUID) });
            queryClient.invalidateQueries({ queryKey: CmsQueryKeys.all });
        },
        onError: (error) => Toast.forError(error, errorMessage),
    });
};

export const usePublishCmsVersion = (cmsMicrositeGUID: string) =>
    usePublishMutation(
        cmsMicrositeGUID,
        (request) => CmsApi({ hideError: true }).publish(cmsMicrositeGUID, request),
        'CMS version published.',
        'Failed to publish CMS version.',
    );
